import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { FileText, Eye, Sparkles } from 'lucide-react';

const ResumeOptionsDialog = ({ open, onOpenChange }) => {
  const navigate = useNavigate();

  const options = [
    {
      id: 'browse-templates',
      title: 'Browse Templates',
      description: 'Choose from professional templates with AI-powered editing. Perfect for quick resume creation with expert designs.',
      icon: FileText,
      color: 'blue',
      path: '/templates',
      isNew: true,
      testId: 'dialog-option-browse-templates'
    },
    {
      id: 'visual-editor',
      title: 'Visual Editor',
      description: 'Create or edit your resume visually in real-time. See changes as you make them with our WYSIWYG editor.',
      icon: Eye,
      color: 'purple',
      path: '/resume/visual-builder',
      testId: 'dialog-option-visual-editor'
    },
    {
      id: 'exact-replica',
      title: 'Exact Replica',
      description: 'Keep your resume format exactly as it is. Upload and preserve the original structure and styling.',
      icon: FileText,
      color: 'sky',
      path: '/resume/upload',
      testId: 'dialog-option-exact-replica'
    },
    {
      id: 'modernize',
      title: 'Modernize and Improve',
      description: 'Start from scratch with AI assistance or upload your resume to enhance it with modern formatting.',
      icon: Sparkles,
      color: 'orange',
      path: '/resume/new',
      testId: 'dialog-option-modernize'
    }
  ];

  const handleOptionClick = (path) => {
    onOpenChange(false);
    navigate(path);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-2xl">Choose How to Create Your Resume</DialogTitle>
          <DialogDescription>
            Select the option that best fits your needs
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          {options.map((option) => {
            const Icon = option.icon;
            return (
              <div
                key={option.id}
                onClick={() => handleOptionClick(option.path)}
                className={`flex items-start p-6 border-2 border-${option.color}-200 rounded-xl hover:border-${option.color}-400 hover:bg-${option.color}-50 cursor-pointer transition-all group`}
                data-testid={option.testId}
              >
                <div className={`w-12 h-12 bg-${option.color}-100 rounded-lg flex items-center justify-center mr-4 group-hover:bg-${option.color}-200`}>
                  <Icon className={`w-6 h-6 text-${option.color}-600`} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className={`font-semibold text-lg text-${option.color}-900`}>{option.title}</h3>
                    {option.isNew && (
                      <span className={`px-2 py-0.5 bg-${option.color}-600 text-white text-xs rounded-full`}>NEW</span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600">{option.description}</p>
                </div>
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ResumeOptionsDialog;
